import React from 'react';
import './widgets.css';

function humanizeWidgetType(widgetType) {
  const raw = String(widgetType || 'information').trim();
  if (!raw) return 'Information';
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function renderChild(child, key) {
  if (!child || typeof child !== 'object' || Array.isArray(child)) {
    return null;
  }
  switch (child.type) {
    case 'Title':
      return <h4 key={key} className="widget-card-title">{child.value}</h4>;
    case 'Badge':
      return (
        <span key={key} className={`widget-badge widget-badge-${child.color || 'primary'}`}>
          {child.label}
        </span>
      );
    case 'Text':
      return (
        <p key={key} className={`widget-text ${child.weight === 'semibold' ? 'widget-text-bold' : ''}`}>
          {child.value}
        </p>
      );
    case 'Caption':
      return <p key={key} className="widget-caption">{child.value}</p>;
    case 'Divider':
      return <hr key={key} className="widget-divider" />;
    case 'Spacer':
      return <div key={key} className="widget-spacer" />;
    default:
      return null;
  }
}

function formatValue(value) {
  if (value == null || value === '') return 'Not provided';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return 'Unrenderable value';
  }
}

function isCodeLike(value) {
  return typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean';
}

function FallbackCard({ data, widgetType, malformed = false, rawPayload = null }) {
  const safeData = data && typeof data === 'object' ? data : {};
  const statusText = safeData.status?.text || humanizeWidgetType(widgetType);
  const structuredChildren = Array.isArray(safeData.children)
    ? safeData.children.filter((child) => child && typeof child === 'object' && !Array.isArray(child))
    : [];
  const hasStructuredChildren = structuredChildren.length > 0;
  const entries = Object.entries(safeData).filter(([key]) => !['children', 'status', 'type', 'size'].includes(key));
  const rawType = rawPayload == null ? '' : typeof rawPayload;

  return (
    <div className="widget-card widget-card-fallback">
      <div className="widget-card-status-header widget-header-neutral">
        <span className="widget-status-dot" />
        <span className="widget-status-text">{statusText}</span>
      </div>

      <div className="widget-card-content">
        {malformed && (
          <>
            <p className="widget-text widget-text-bold">We could not parse this widget payload.</p>
            <p className="widget-caption">
              The message is still available, but this widget format is invalid.
              {rawType ? ` Payload type: ${rawType}.` : ''}
            </p>
          </>
        )}

        {hasStructuredChildren && structuredChildren.map((child, idx) => renderChild(child, idx))}

        {!hasStructuredChildren && entries.length > 0 && (
          <div className="widget-fallback-grid">
            {entries.map(([key, value]) => (
              <div key={key} className="widget-fallback-row">
                <span className="widget-fallback-label">{humanizeWidgetType(key)}</span>
                {isCodeLike(value) ? (
                  <code className="widget-fallback-code">{formatValue(value)}</code>
                ) : (
                  <span className="widget-text">{formatValue(value)}</span>
                )}
              </div>
            ))}
          </div>
        )}

        {!hasStructuredChildren && entries.length === 0 && !malformed && (
          <p className="widget-caption">No additional details were provided.</p>
        )}
      </div>
    </div>
  );
}

export default FallbackCard;
